/**
 * TNWS favorites nudges:
 * - Home page tip that invites shoppers to use the heart (auto-hides after 8s).
 * - "Added to favorites" toast the first time a shopper saves a watch.
 * - A heart that flies to the header favorites icon on every save, then a pulse.
 */
(() => {
  const FIRST_SAVE_KEY = 'tnws:fav-first-save-seen';
  const TIP_COUNT_KEY = 'tnws:fav-tip-shown';
  const TIP_SESSION_KEY = 'tnws:fav-tip-session';
  const TIP_MAX_SHOWS = 3;
  const TIP_DELAY_MS = 2500;
  const TIP_VISIBLE_MS = 8000;

  const HEART_SVG =
    '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false"><path d="M12 20.3s-7.6-4.6-9.3-9.4C1.6 7.6 3.7 4.5 7 4.5c2 0 3.6 1.1 5 3 1.4-1.9 3-3 5-3 3.3 0 5.4 3.1 4.3 6.4-1.7 4.8-9.3 9.4-9.3 9.4z" fill="currentColor"/></svg>';

  const storage = {
    get(key, session = false) {
      try {
        return (session ? sessionStorage : localStorage).getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value, session = false) {
      try {
        (session ? sessionStorage : localStorage).setItem(key, value);
      } catch (e) {
        /* storage unavailable */
      }
    },
  };

  const favoritesLink = () => document.getElementById('tnws-favorites-link');
  const favoritesUrl = () => favoritesLink()?.getAttribute('href') || '/pages/favorites';
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const hasFavorites = () => {
    try {
      if (typeof TnwsFavorites !== 'undefined') return TnwsFavorites.getItems().length > 0;
    } catch (e) {
      /* favorites script unavailable */
    }
    return false;
  };

  const isVisible = (el) => {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
  };

  /* ---------- toast ---------- */

  let toastEl = null;
  let toastTimer = null;

  const hideToast = () => {
    if (!toastEl) return;
    toastEl.classList.remove('is-visible');
    window.clearTimeout(toastTimer);
    const el = toastEl;
    window.setTimeout(() => {
      if (!el.classList.contains('is-visible')) el.remove();
    }, 350);
    toastEl = null;
  };

  const showToast = ({ title, text, actionLabel, actionHref, variant, duration }) => {
    hideToast();
    const el = document.createElement('div');
    el.className = `tnws-fav-toast tnws-fav-toast--${variant}`;
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.innerHTML = `
      <span class="tnws-fav-toast__icon">${HEART_SVG}</span>
      <span class="tnws-fav-toast__copy">
        <span class="tnws-fav-toast__title"></span>
        <span class="tnws-fav-toast__text"></span>
      </span>
      ${actionHref ? '<a class="tnws-fav-toast__action"></a>' : ''}
      <button type="button" class="tnws-fav-toast__close" aria-label="Dismiss">
        <svg viewBox="0 0 18 18" width="14" height="14" aria-hidden="true" focusable="false"><path stroke="currentColor" stroke-width="1.4" stroke-linecap="round" d="M4.5 4.5l9 9M13.5 4.5l-9 9"/></svg>
      </button>`;
    el.querySelector('.tnws-fav-toast__title').textContent = title;
    el.querySelector('.tnws-fav-toast__text').textContent = text;
    const action = el.querySelector('.tnws-fav-toast__action');
    if (action) {
      action.textContent = actionLabel;
      action.href = actionHref;
    }
    el.querySelector('.tnws-fav-toast__close').addEventListener('click', hideToast);
    el.addEventListener('mouseenter', () => window.clearTimeout(toastTimer));
    el.addEventListener('mouseleave', () => {
      toastTimer = window.setTimeout(hideToast, 2500);
    });

    document.body.appendChild(el);
    toastEl = el;
    requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('is-visible')));
    toastTimer = window.setTimeout(hideToast, duration);
  };

  /* ---------- fly-to-header animation ---------- */

  const pulseHeader = () => {
    const link = favoritesLink();
    if (!link) return;
    link.classList.remove('tnws-fav-bump');
    void link.offsetWidth;
    link.classList.add('tnws-fav-bump');
    window.setTimeout(() => link.classList.remove('tnws-fav-bump'), 900);
  };

  const flyToHeader = (button) => {
    const target = favoritesLink()?.querySelector('.tudor-header__icon') || favoritesLink();
    if (!button || !isVisible(target) || prefersReducedMotion()) {
      pulseHeader();
      return;
    }

    const from = button.getBoundingClientRect();
    const to = target.getBoundingClientRect();
    const heart = document.createElement('span');
    heart.className = 'tnws-fav-fly';
    heart.innerHTML = HEART_SVG;
    heart.style.left = `${from.left + from.width / 2 - 11}px`;
    heart.style.top = `${from.top + from.height / 2 - 11}px`;
    document.body.appendChild(heart);

    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + to.height / 2 - (from.top + from.height / 2);
    const lift = Math.min(-60, dy * 0.35);

    const animation = heart.animate(
      [
        { transform: 'translate(0, 0) scale(1)', opacity: 1 },
        { transform: `translate(${dx * 0.45}px, ${dy * 0.45 + lift}px) scale(1.25)`, opacity: 1, offset: 0.45 },
        { transform: `translate(${dx}px, ${dy}px) scale(0.55)`, opacity: 0.2 },
      ],
      { duration: 820, easing: 'cubic-bezier(0.45, 0, 0.2, 1)' }
    );
    animation.onfinish = () => {
      heart.remove();
      pulseHeader();
    };
  };

  document.addEventListener('tnws:favorite-added', (event) => {
    flyToHeader(event.detail?.button);

    if (storage.get(FIRST_SAVE_KEY)) return;
    storage.set(FIRST_SAVE_KEY, '1');
    hideTip();
    window.setTimeout(
      () =>
        showToast({
          variant: 'saved',
          title: 'Added to favorites',
          text: 'Find every watch you save under the heart up top.',
          actionLabel: 'View',
          actionHref: favoritesUrl(),
          duration: 5000,
        }),
      prefersReducedMotion() ? 0 : 450
    );
  });

  /* ---------- home page tip ---------- */

  let tipShowing = false;

  const hideTip = () => {
    if (!tipShowing) return;
    tipShowing = false;
    hideToast();
  };

  const blockingDialogOpen = () =>
    [...document.querySelectorAll('[role="dialog"], [aria-modal="true"], .popup-modal, details[open] > .modal-overlay')].some(
      (el) => {
        if (el.closest('.tnws-fav-toast, .tnws-shop-drawer, .menu-drawer')) return false;
        const rect = el.getBoundingClientRect();
        const style = getComputedStyle(el);
        return rect.width > 240 && rect.height > 160 && style.visibility !== 'hidden' && style.display !== 'none';
      }
    ) || document.body.classList.contains('overflow-hidden');

  const maybeShowTip = (attempt = 0) => {
    if (hasFavorites() || storage.get(FIRST_SAVE_KEY)) return;
    if (blockingDialogOpen()) {
      if (attempt < 4) window.setTimeout(() => maybeShowTip(attempt + 1), 4000);
      return;
    }
    if (!document.querySelector('[data-tnws-favorite]')) return;

    tipShowing = true;
    storage.set(TIP_SESSION_KEY, '1', true);
    storage.set(TIP_COUNT_KEY, String(Number(storage.get(TIP_COUNT_KEY) || 0) + 1));
    showToast({
      variant: 'tip',
      title: 'Save the ones you love',
      text: 'Tap the heart on any watch to keep it in your favorites.',
      duration: TIP_VISIBLE_MS,
    });
    document.querySelectorAll('[data-tnws-favorite]').forEach((button) => {
      if (isVisible(button)) button.classList.add('tnws-fav-hint');
    });
    window.setTimeout(() => {
      document.querySelectorAll('.tnws-fav-hint').forEach((button) => button.classList.remove('tnws-fav-hint'));
    }, 2600);
  };

  const initTip = () => {
    if (document.body.dataset.tnwsTemplate !== 'index') return;
    if (storage.get(TIP_SESSION_KEY, true)) return;
    if (Number(storage.get(TIP_COUNT_KEY) || 0) >= TIP_MAX_SHOWS) return;
    window.setTimeout(() => maybeShowTip(), TIP_DELAY_MS);
  };

  if (document.readyState === 'complete') initTip();
  else window.addEventListener('load', initTip, { once: true });
})();
